/**
 * Voorraadmeldingen in de werkplek (sessie 48, F3).
 *
 * De pushmelding "Inkoopsignaal voorraadmeldingen" opende nog /oud. Dit
 * kleine scherm onder Systeem toont hetzelfde als het oude beheer, in de
 * werkplek: per product hoeveel klanten wachten en of het besteld is, en
 * daaronder de wachtlijst. Alleen lezen; bestellen gebeurt in Stock.
 */
import { useQuery } from "@tanstack/react-query";

import { voorraadmeldingenApi } from "@/lib/api";

import { MetCopiloot } from "./Copiloot/MetCopiloot";
import "./Vandaag/vandaag.css";

function datum(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    timeZone: "Europe/Amsterdam",
  });
}

function besteldTekst(besteld: boolean | null): { tekst: string; alarm: boolean } {
  if (besteld === true) return { tekst: "Besteld", alarm: false };
  if (besteld === false) return { tekst: "Niet besteld", alarm: true };
  return { tekst: "Onbekend (Stock niet bereikbaar)", alarm: false };
}

export function WerkplekVoorraad() {
  const vraag = useQuery({
    queryKey: ["werkplek", "voorraadmeldingen"],
    queryFn: () => voorraadmeldingenApi.overzicht(30),
  });
  const data = vraag.data;
  const nietBesteld = (data?.per_product ?? []).filter((p) => p.besteld === false);

  return (
    <>
      <div className="wp-bovenbalk">
        <h1 style={{ fontSize: 17, margin: 0, fontWeight: 600 }}>Voorraadmeldingen</h1>
        {data ? (
          <span style={{ color: "var(--wp-tekst2)", fontSize: 13 }} data-testid="wp-voorraad-kop">
            {data.open_aantal} wachtend · {nietBesteld.length} product
            {nietBesteld.length === 1 ? "" : "en"} niet besteld
          </span>
        ) : null}
      </div>
      <div className="wp-inhoud">
        <MetCopiloot pagina="voorraad">
          <div
            style={{ overflowY: "auto", padding: 18, maxWidth: 900, width: "100%", marginInline: "auto" }}
            data-testid="wp-voorraad"
          >
            {vraag.isLoading ? (
              <p style={{ color: "var(--wp-tekst2)" }}>Bezig met laden…</p>
            ) : vraag.isError || !data ? (
              <p role="alert" style={{ color: "var(--wp-rood)" }}>
                De voorraadmeldingen zijn niet op te halen. Ververs de pagina;
                blijft dit staan, dan is de API niet bereikbaar.
              </p>
            ) : (
              <>
                <div className="wp-blok" style={{ padding: "12px 14px", marginBottom: 12 }}>
                  <h2 style={{ margin: "0 0 8px", fontSize: 15 }}>Per product</h2>
                  {!data.stock_gekoppeld ? (
                    <p className="wp-leegregel">
                      Stock is niet gekoppeld: of een product besteld is, is dan
                      onbekend.
                    </p>
                  ) : null}
                  {data.per_product.length === 0 ? (
                    <p className="wp-leegregel">Er wacht op dit moment niemand op een product.</p>
                  ) : (
                    <table className="wp-voorraad-tabel" data-testid="wp-voorraad-producten">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Wachtend</th>
                          <th>Inkoop</th>
                          <th>Verwacht</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.per_product.map((p) => {
                          const stand = besteldTekst(p.besteld);
                          return (
                            <tr key={p.lightspeed_product_id} data-alarm={stand.alarm ? "ja" : "nee"}>
                              <td style={{ overflowWrap: "anywhere" }}>{p.product_naam}</td>
                              <td>{p.wachtend}</td>
                              <td>
                                <span className={`wp-chip ${stand.alarm ? "wp-chip-risico" : "wp-chip-grijs"}`}>
                                  {stand.tekst}
                                </span>
                              </td>
                              <td>{p.verwachting_tekst ?? (p.verwacht_op ? datum(p.verwacht_op) : "")}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="wp-blok" style={{ padding: "12px 14px" }}>
                  <h2 style={{ margin: "0 0 8px", fontSize: 15 }}>
                    Wachtlijst ({data.open_aantal} open, {data.gemaild_aantal} gemaild)
                  </h2>
                  {data.rijen.length === 0 ? (
                    <p className="wp-leegregel">De wachtlijst is leeg.</p>
                  ) : (
                    <ul className="wp-voorraad-lijst">
                      {data.rijen.slice(0, 200).map((r) => (
                        <li key={r.id}>
                          <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                            {r.product_naam}
                            {r.variant_titel ? ` (${r.variant_titel})` : ""}
                          </span>
                          <span className="wp-leegregel">
                            {r.status === "open" ? "wacht sinds" : r.status} {datum(r.aangemaakt_op)}
                            {r.gemaild_op ? ` · gemaild ${datum(r.gemaild_op)}` : ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </MetCopiloot>
      </div>
    </>
  );
}
